// client/src/dnd/components/pixi_parts/SpellOverlay.js
import React, { useRef, useEffect, useCallback } from "react";
import * as PIXI from "pixi.js";
import { useMap } from "react-leaflet";
import { createEmitterRuntime } from "../../emitter_tool/runtime/RuntimeBridge.js";
import { registerSpellRuntime } from "./SpellManager.js";

export default function SpellOverlay(props) {
  const { imageSize, gridConfig } = props;

  const map = useMap();

  const spellContainerRef = useRef(null);
  const spellAppRef = useRef(null);
  const spellRuntimeRef = useRef(null);

  const resizeAndTransform = useCallback(() => {
    const app = spellAppRef.current;
    if (!app || !map || !imageSize) return;
    if (!app.stage) return;

    const container = map.getContainer?.();
    if (!container) return;

    // Match Leaflet container size
    app.renderer.resize(container.clientWidth, container.clientHeight);

    // CRS.Simple → image coords
    const imgW = Number(imageSize?.[0]) || 0;
    const imgH = Number(imageSize?.[1]) || 0;

    const p0 = map.latLngToContainerPoint([0, 0]);
    const pX = map.latLngToContainerPoint([0, imgW]);
    const pY = map.latLngToContainerPoint([imgH, 0]);

    const sx = imgW > 0 ? (pX.x - p0.x) / imgW : 1;
    const sy = imgH > 0 ? (pY.y - p0.y) / imgH : 1;

    app.stage.position.set(p0.x, p0.y);
    app.stage.scale.set(sx, sy);

    // Expose transform for any glow / darkness integration that wants the app
    try {
      window.__PA_SPELL_APP = app;
    } catch {}
  }, [map, imageSize]);

  // ─────────────────────────────────────
  // Create PIXI app + runtime (RuntimeBridge)
  // ─────────────────────────────────────
  useEffect(() => {
    let destroyed = false;

    function makeSpellApp() {
      if (!spellContainerRef.current || spellAppRef.current) return;

      const app = new PIXI.Application({
        width: 0,
        height: 0,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        backgroundAlpha: 0,
        autoStart: false,
        powerPreference: "high-performance",
        clearBeforeRender: true,
      });

      const canvas = app.view;
      Object.assign(canvas.style, {
        position: "absolute",
        top: "0",
        left: "0",
        pointerEvents: "none",
        width: "100%",
        height: "100%",
        background: "transparent",
      });

      app.stage.sortableChildren = true;

      const particlesLayer = new PIXI.Container();
      particlesLayer.sortableChildren = true;
      particlesLayer.zIndex = 550;
      app.stage.addChild(particlesLayer);
      app.particlesLayer = particlesLayer;

      if (!destroyed && spellContainerRef.current) {
        spellContainerRef.current.appendChild(canvas);
        spellAppRef.current = app;

        const ppm =
          (gridConfig && (gridConfig.metre_in_pixels || gridConfig.meter_in_pixels)) ||
          80;

        // RuntimeBridge runtime (thin wrapper around UnifiedRuntime)
        const runtime = createEmitterRuntime({
          PIXIRef: PIXI,
          app,
          pxPerMeter: ppm,
        });

        spellRuntimeRef.current = runtime;

        // Initial transform
        resizeAndTransform();
      } else {
        try {
          app.destroy(true);
        } catch {}
      }
    }

    makeSpellApp();

    return () => {
      destroyed = true;

      const runtime = spellRuntimeRef.current;
      if (runtime) {
        try {
          runtime.destroy();
        } catch {}
        spellRuntimeRef.current = null;
      }

      const app = spellAppRef.current;
      if (app) {
        try {
          app.destroy(true);
        } catch {}
        spellAppRef.current = null;
      }

      try {
        if (window.__PA_SPELL_APP === app) {
          window.__PA_SPELL_APP = null;
        }
      } catch {}
    };
  }, [gridConfig, resizeAndTransform]);

  // ─────────────────────────────────────
  // Register runtime with SpellManager
  // ─────────────────────────────────────
  useEffect(() => {
    const runtime = spellRuntimeRef.current;
    const app = spellAppRef.current;
    if (!runtime || !app) return;

    // Same imageSize fallback logic SpellManager expects
    const getImageSize = () => {
      if (Array.isArray(imageSize)) {
        const w = Number(imageSize[0]) || 1920;
        const h = Number(imageSize[1]) || 1080;
        return [w, h];
      }
      const w = Number(imageSize?.width) || 1920;
      const h = Number(imageSize?.height) || 1080;
      return [w, h];
    };

    const getWallPolygons = () => {
      if (typeof window === "undefined") return null;
      try {
        if (window.__PA_WALLS) return window.__PA_WALLS;
      } catch {}
      return null;
    };

    registerSpellRuntime({
      runtime,
      app,
      getImageSize,
      gridConfig: gridConfig || {},
      getWallPolygons,
      wallPolygons: null,
    });
  }, [imageSize, gridConfig]);

  // ─────────────────────────────────────
  // Keep runtime pxPerMeter in sync (SpellManager will still override per-cast)
  // ─────────────────────────────────────
  useEffect(() => {
    const ppm =
      (gridConfig && (gridConfig.metre_in_pixels || gridConfig.meter_in_pixels)) ||
      80;

    const r = spellRuntimeRef.current;

    if (r && typeof r.setEnvironment === "function") {
      try {
        r.setEnvironment({ pxPerMeter: ppm });
      } catch {}
    }
  }, [gridConfig]);

  // ─────────────────────────────────────
  // Follow map transforms (zoom/pan/resize)
  // ─────────────────────────────────────
  useEffect(() => {
    if (!map || !imageSize) return;

    function updateSpellApp() {
      resizeAndTransform();
    }

    updateSpellApp();

    map.on("zoom viewreset resize move", updateSpellApp);
    return () => {
      map.off("zoom viewreset resize move", updateSpellApp);
    };
  }, [map, imageSize, resizeAndTransform]);

  // ─────────────────────────────────────
  // RAF: drive runtime + render stage
  // ─────────────────────────────────────
  const drawSpells = useCallback(() => {
    const app = spellAppRef.current;
    if (!app || !map || !map._loaded) {
      requestAnimationFrame(drawSpells);
      return;
    }

    const runtime = spellRuntimeRef.current;
    if (runtime && typeof runtime.update === "function") {
      try {
        // EmitterStage uses dt in seconds; 1/60 is a good fixed step
        runtime.update(1 / 60);
      } catch {}
    }

    try {
      app.renderer.render(app.stage);
    } catch {}
    requestAnimationFrame(drawSpells);
  }, [map]);

  useEffect(() => {
    const id = requestAnimationFrame(drawSpells);
    return () => cancelAnimationFrame(id);
  }, [drawSpells]);

  // NOTE: we do NOT hook map clicks here anymore.
  // PixiLayer is already handling map clicks and calling castPendingSpellAtTarget,
  // which ultimately invokes SpellManager.castSpellById → runtime.runPreset.

  return (
    <div
      ref={spellContainerRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
        width: "100%",
        height: "100%",
        // This is what you had when spells *were* rendering.
        // It sits under DarknessOverlay (650) and FogOfWarOverlay (600) by default.
        zIndex: 550,
        backgroundColor: "transparent",
      }}
    />
  );
}
